'use client';

import { useEvent, useStoreMap } from 'effector-react';
import { UploadArea } from '@nx-frontend/shared/ui/upload-area';
import { setLastPublishTimeEvent } from '@nx-frontend/shared/store/change-events-store';
import { getFilesByTargetId } from '@nx-frontend/shared/store/feature-files';
import { PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { FileListItem, FileListItemColumn } from '../file-list-item';
import { useFilesUpload } from '../hooks/useFilesUpload';

interface FilesBucketProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  context: {
    sessionId: string | null;
    portalId: string;
    workspaceId: string;
    blockId: string;
    pageId: string;
  };
  userId: number;
  fileOpenHost: string;
  orgRole: string;
  canEditOtherUsersFile?: boolean;
  deleteDisabled?: boolean;
  renameDisabled?: boolean;
}
export function FilesBucket({
  themeColors,
  themeStyle,
  context,
  orgRole,
  userId,
  fileOpenHost,
  canEditOtherUsersFile,
  deleteDisabled,
  renameDisabled,
}: FilesBucketProps) {
  const [setLastPublishTime] = useEvent([setLastPublishTimeEvent]);
  const files = useStoreMap(getFilesByTargetId(context.blockId));
  const fileIds = files.map((file) => file.globalId);
  const filesUpload = useFilesUpload({
    autoCloseUploadModal: true,
    onSuccess: () => {
      const time = new Date().getTime();
      setLastPublishTime(time);
    },
  });
  const sessionId = context.sessionId;
  const handleFileSelect = (files: FileList | null) => {
    if (files && sessionId) {
      const filesArr = Array.from(files);
      filesUpload({ files: filesArr, context: { ...context, sessionId } });
    }
  };
  const isEmpty = !fileIds.length;
  const showUploadButton = orgRole !== 'visitor';

  return (
    <div className="w-full">
      <div className="flex flex-col w-full max-h-64 overflow-auto">
        {fileIds.map((id) => {
          return (
            <FileListItem
              key={id}
              id={id}
              themeColors={themeColors}
              themeStyle={themeStyle}
              visibleColumns={[FileListItemColumn.NAME, FileListItemColumn.MENU]}
              hideDestination={true}
              fileOpenHost={fileOpenHost}
              showCheckForNewFiles={false}
              orgRole={orgRole}
              userId={userId}
              itemCustomClass="sm:px-3"
              hasBorder={false}
              showMenuOnHover={true}
              openFileLocationEnabled={false}
              columnWidths={{
                name: '95%',
                menu: '32px',
              }}
              showNewFileDot={false}
              canCreateEsign={false}
              canSubmitEsign={false}
              canEditOtherUsersFile={canEditOtherUsersFile}
              renameDisabled={renameDisabled}
              deleteDisabled={deleteDisabled}
            />
          );
        })}
      </div>
      <div className="w-full p-[2px]">
        <UploadArea
          extendedView={isEmpty}
          themeColors={themeColors}
          themeStyle={themeStyle}
          disabled={!showUploadButton}
          onFilesSelect={handleFileSelect}
        />
      </div>
    </div>
  );
}
